import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ParentContextBannerComponent } from '../parent-context-banner/parent-context-banner.component';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { BillAiFlowEnergyPlanPromptComponent } from '../bill-ai-flow-energy-plan-prompt/bill-ai-flow-energy-plan-prompt.component';
import { BillAiFlowBillReviewStepComponent } from '../bill-ai-flow-bill-review-step/bill-ai-flow-bill-review-step.component';
import { formatBillPeriod } from '../../../utils/date-format.util';
import { ParsedBillData } from '../../../models';
import { validateBillReviewStep, dualSupplyWithoutSupplier } from '../bill-ai-flow-bill-review-step/bill-ai-flow-bill-review-step.validation';
import { SummaryGroup, groupErrorsBySummary } from '../validation';
import { SUPPLIER_AUTO_ACCEPT_SCORE } from '../../../utils/match-score.util';

interface OverviewBillRow {
  billGroupId: string;
  billIndex: number;
  meterUan: string;
  saAddress: string;
  billPeriod: string;
  provider: string;
  amount: number;
  usage: number;
  usageUnit: string;
  confidence: number;
  warnings: string[];
  /** Outcome for a bill matching an existing bill: skipped, replacing it or merging into it */
  outcome: { tone: 'skip' | 'replace' | 'merge'; icon: string; label: string; title: string } | null;
  hasSupplier: boolean;
  isDual: boolean;
}

@Component({
  selector: 'app-bill-ai-flow-bill-overview',
  standalone: true,
  imports: [
    CommonModule,
    ParentContextBannerComponent,
    BillAiFlowEnergyPlanPromptComponent,
    BillAiFlowBillReviewStepComponent,
  ],
  templateUrl: './bill-ai-flow-bill-overview.component.html',
  styleUrl: './bill-ai-flow-bill-overview.component.scss'
})
export class BillAiFlowBillOverviewComponent implements OnInit {
  phase = signal<'overview' | 'detail' | 'energy-plans'>('overview');
  billRows: OverviewBillRow[] = [];
  expandedWarnings: Record<string, boolean> = {};

  totalBills = 0;
  totalAmount = 0;
  totalUsage = 0;
  /** Unit label for the usage stat — a single shared unit, or 'usage' when bills mix units (e.g. kWh + therms). */
  usageUnitLabel = 'kWh';
  /** True when bills mix units — the summed usage stat is meaningless and hidden. */
  usageUnitsMixed = false;
  warningCount = 0;

  // Validation state (see guides/ai-onboarding/FIELD-VALIDATION.md)
  validationGroups: SummaryGroup[] = [];
  hasValidationBlockers = false;

  readonly state = inject(BillAiFlowStore);

  hasEnergyPlanBills = computed(() => {
    const response = this.state.matchResponse();
    const billData = this.state.billData();
    const skipped = this.state.skippedBillGroupIds();
    if (!response) return false;

    for (const len of response.legalEntities) {
      for (const sa of len.serviceAddresses) {
        for (const meter of sa.meters) {
          for (const bill of meter.bills) {
            if (skipped.has(bill.groupId)) continue;
            if (bill.supplier?.supplierId != null) {
              const supplier = bill.supplier;
              const data = billData[bill.groupId];
              // Bill needs attention if it has a supplier but no energyPlanId
              if (!data?.energyPlanId && supplier.energyPlanId == null) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  });

  ngOnInit(): void {
    this.buildBillRows();
    // Arriving from a summary "Change" / "Review" link: open that bill straight away
    if (this.state.consumeOpenBillDetailRequest()) {
      this.phase.set('detail');
    }
  }

  private buildBillRows(): void {
    const response = this.state.matchResponse();
    const matchRequest = this.state.matchRequest();
    const billData = this.state.billData();
    const billNav = this.state.getBillNavigation();
    if (!response || !matchRequest) return;

    let billIndex = 0;

    for (const len of response.legalEntities) {
      for (const sa of len.serviceAddresses) {
        const saRequest = matchRequest.legalEntities
          .find(l => l.groupId === len.groupId)?.serviceAddresses
          .find(s => s.groupId === sa.groupId);
        const saAddr = saRequest
          ? [saRequest.street1, saRequest.city, saRequest.state].filter(Boolean).join(', ')
          : sa.groupId;

        for (const meter of sa.meters) {
          const meterRequest = saRequest?.meters.find(m => m.groupId === meter.groupId);
          const meterUan = meterRequest?.utilityAccountNumber1 || meter.groupId;

          for (const bill of meter.bills) {
            const data = billData[bill.groupId];
            if (!data) { billIndex++; continue; }

            const outcome = this.duplicateOutcome(bill.groupId);
            const skipped = outcome?.tone === 'skip';
            // A skipped bill isn't imported, so its data warnings don't need fixing
            const warnings = skipped ? [] : this.computeWarnings(data, bill);

            const period = formatBillPeriod(data.billingPeriodStart, data.billingPeriodEnd);

            this.billRows.push({
              billGroupId: bill.groupId,
              billIndex,
              meterUan,
              saAddress: saAddr,
              billPeriod: period,
              provider: dualSupplyWithoutSupplier(data) ? 'Supplier needed' : (data.providerName || 'On Utility'),
              amount: data.totalAmount || 0,
              usage: data.usageAmount || 0,
              usageUnit: data.usageUnit?.trim() || 'kWh',
              confidence: data.confidence || 0,
              warnings,
              outcome,
              hasSupplier: bill.supplier?.supplierId != null,
              isDual: !!data.isDualBilling,
            });

            if (!skipped) {
              this.totalBills++;
              this.totalAmount += data.totalAmount || 0;
              this.totalUsage += data.usageAmount || 0;
            }
            if (warnings.length > 0) this.warningCount++;

            billIndex++;
          }
        }
      }
    }

    // Compare units case-insensitively ("CCF" and "ccf" are one unit); show the first row's spelling.
    const distinctUnits = new Set(this.billRows.map(r => r.usageUnit.toLowerCase()));
    this.usageUnitsMixed = distinctUnits.size > 1;
    // Mixed units hide the summed stat, so the label shown is always a single unit's — kWh until there are rows.
    this.usageUnitLabel = distinctUnits.size === 1 ? this.billRows[0].usageUnit : 'kWh';
  }

  private computeWarnings(data: ParsedBillData, matchBill: any): string[] {
    const warnings: string[] = [];

    // Data completeness
    if (!data.billingPeriodStart) warnings.push('Missing billing start date');
    if (!data.billingPeriodEnd) warnings.push('Missing billing end date');
    if (!data.totalAmount || data.totalAmount === 0) warnings.push('Total amount is $0');
    if (!data.usageAmount || data.usageAmount === 0) warnings.push(`Usage is 0 ${data.usageUnit || 'kWh'}`);
    if (!data.utilityName?.trim()) warnings.push('Missing utility name');

    // Confidence
    if (data.confidence > 0 && data.confidence < 0.6) {
      warnings.push(`Low extraction confidence (${Math.round(data.confidence * 100)}%)`);
    }

    // Low supplier confidence — uses SUPPLIER_AUTO_ACCEPT_SCORE (0.80,
    // intentionally more forgiving than entity AUTO_ACCEPT_SCORE because
    // supplier-name matching is inherently fuzzier).
    if (matchBill?.supplier?.supplierId && matchBill.supplier.confidence < SUPPLIER_AUTO_ACCEPT_SCORE) {
      warnings.push(`Low supplier confidence (${Math.floor(matchBill.supplier.confidence * 100)}%)`);
    }

    // Arithmetic checks (5% tolerance, only when both sides non-zero)
    this.checkArithmetic(warnings, data);

    // Overlaps block the import; exact duplicates and merges are outcomes, not warnings
    const duplicate = matchBill ? this.state.getBillDuplicate(matchBill.groupId) : null;
    if (duplicate && !this.duplicateOutcome(matchBill.groupId)) {
      const ot = duplicate.overlapType;
      if (ot === 'PARTIAL' || ot === 'CONTAINS' || ot === 'CONTAINED_BY') {
        warnings.push('Overlapping — dates conflict with an existing bill');
      }
    }

    return warnings;
  }

  private duplicateOutcome(billGroupId: string): OverviewBillRow['outcome'] {
    const duplicate = this.state.getBillDuplicate(billGroupId);
    if (!duplicate) return null;
    const id = duplicate.existingBillId;
    if (duplicate.pairable && !this.state.isPairRejected(billGroupId)) {
      const side = duplicate.pairSide === 'SUPPLY' ? 'supply' : 'distribution';
      return { tone: 'merge', icon: 'merge', label: 'Merges', title: `Merges into bill #${id} — adds the ${side} side` };
    }
    const decision = this.state.duplicateDecisions().find(d => d.billGroupId === billGroupId);
    if (!decision) return null;
    return decision.action === 'REPLACE'
      ? { tone: 'replace', icon: 'swap_horiz', label: 'Replaces', title: `Replaces bill #${id}` }
      : { tone: 'skip', icon: 'skip_next', label: 'Skipped', title: `Already imported as bill #${id} — will be skipped` };
  }

  private checkArithmetic(warnings: string[], data: ParsedBillData): void {
    const tolerance = 0.05;
    const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Supply: charges + taxes ≈ total
    if (data.supplyCharges > 0 && data.supplyTotalCost > 0) {
      const expected = data.supplyCharges + (data.supplyTaxes || 0);
      const diff = Math.abs(expected - data.supplyTotalCost);
      if (diff / data.supplyTotalCost > tolerance) {
        warnings.push(`Supply off by $${fmt(diff)} — charges ($${fmt(data.supplyCharges)}) + tax ($${fmt(data.supplyTaxes || 0)}) = $${fmt(expected)}, but supply total is $${fmt(data.supplyTotalCost)}`);
      }
    }

    // Distribution: charges + taxes ≈ total
    if (data.distributionCharges > 0 && data.distributionTotalCost > 0) {
      const expected = data.distributionCharges + (data.distributionTaxes || 0);
      const diff = Math.abs(expected - data.distributionTotalCost);
      if (diff / data.distributionTotalCost > tolerance) {
        warnings.push(`Distribution off by $${fmt(diff)} — charges ($${fmt(data.distributionCharges)}) + tax ($${fmt(data.distributionTaxes || 0)}) = $${fmt(expected)}, but distribution total is $${fmt(data.distributionTotalCost)}`);
      }
    }

    // Total: supply + distribution ≈ total amount
    if (data.supplyTotalCost > 0 && data.distributionTotalCost > 0 && data.totalAmount > 0) {
      const expected = data.supplyTotalCost + data.distributionTotalCost;
      const diff = Math.abs(expected - data.totalAmount);
      if (diff / data.totalAmount > tolerance) {
        warnings.push(`Total off by $${fmt(diff)} — supply ($${fmt(data.supplyTotalCost)}) + distribution ($${fmt(data.distributionTotalCost)}) = $${fmt(expected)}, but bill total is $${fmt(data.totalAmount)}`);
      }
    }
  }

  toggleWarnings(billGroupId: string): void {
    this.expandedWarnings[billGroupId] = !this.expandedWarnings[billGroupId];
  }

  openBillDetail(row: OverviewBillRow): void {
    // Navigate the store to the correct bill index so the bill-review-step opens the right bill
    this.state.setCurrentBillIndex(row.billIndex);
    this.phase.set('detail');
  }

  backToOverview(): void {
    // Rebuild rows in case user edited the bill
    this.billRows = [];
    this.totalBills = 0;
    this.totalAmount = 0;
    this.totalUsage = 0;
    this.warningCount = 0;
    this.buildBillRows();
    this.phase.set('overview');
  }

  continue(): void {
    // Run bill validation before advancing. The banner auto-shows when
    // hasValidationBlockers is true, so we just rebuild and bail if blocked.
    const result = validateBillReviewStep({
      billData: this.state.billData(),
      billNavigation: this.state.billNavigation(),
      excludeBillGroupIds: this.state.skippedBillGroupIds(),
      isNewEntity: (gid) => this.state.isNewEntity(gid),
    });
    this.validationGroups = groupErrorsBySummary(result.errors);
    this.hasValidationBlockers = result.hasBlockers;

    if (result.hasBlockers) return;

    if (this.hasEnergyPlanBills() && this.phase() === 'overview') {
      this.phase.set('energy-plans');
      return;
    }
    this.state.completeStep('bill-review');
    this.state.goToStep('summary');
  }

  goBack(): void {
    this.state.goBack();
  }
}
